import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { useSearchParams } from 'react-router-dom';
import './QuotationsModule.css';
import Swal from 'sweetalert2';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import {
    Plus, Download, Filter, Search, MoreVertical, MoreHorizontal,
    Edit, Trash2, Copy, Send, X, Calendar, Table, Printer,
    Phone, Eye, Edit3, Banknote, XCircle, FileCheck,
    ArrowLeft, ChevronDown, ChevronUp, User, GripVertical, FileText,
    Settings
} from 'lucide-react';
import { Icon } from '@iconify/react';
import { PiExport } from 'react-icons/pi';
import Loader from '../../../components/Loader/Loader';
import QuotationActionFilteration from './QuotationActionFilteration';
import QuotationAdvancedFilter from './QuotationAdvancedFilter';
import DynamicCalendar from '../../../components/DynamicCalendar/DynamicCalendar';
import { parseCustomDate } from '../../../utils/dateUtils';
import { LeadDetails as CommonLeadDetails } from '../../../components/common/LeadDetails/LeadDetails';
import SubHeader from '../../../components/common/SubHeader/SubHeader';
import ScoreCard from '../../../components/common/ScoreCard/ScoreCard';
import usePermissions from '../../Master_Settings/usePermissions';

// Premium Components & MUI
import PremiumModuleTable from '../../Task/Components/common/PremiumModuleTable';
import PremiumModuleCards from '../../Task/Components/common/PremiumModuleCards';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

const QuotationsModule = () => {
    const { permissions } = usePermissions('PAG10222');
    const crmOperationPerms = useMemo(() => {
        if (!permissions || permissions.length === 0) return null;
        return permissions[0];
    }, [permissions]);

    const [searchParams, setSearchParams] = useSearchParams();
    const [allQuotations, setAllQuotations] = useState([]); // Raw data source
    const [loading, setLoading] = useState(true);
    const [hasFetched, setHasFetched] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [activeDropdownId, setActiveDropdownId] = useState(null);
    const [viewMode, setViewMode] = useState('table');
    const [statusFilters, setStatusFilters] = useState([]);

    const getStandardStatus = (rawStatus) => {
        const s = String(rawStatus || '').toLowerCase();
        if (s.includes('converted') || s.includes('approved')) return 'Approved';
        if (s.includes('draft') || s.includes('open')) return 'Draft';
        if (s.includes('sent') || s.includes('pending')) return 'Sent For Approval';
        if (s.includes('canc') || s.includes('expir') || s.includes('rej') || s.includes('delete')) return 'Cancelled';
        return 'Draft'; // Default
    };

    const advanceFilterFields = [
        { label: 'Quotation Number', value: 'QuoteNumber' },
        { label: 'Customer Name', value: 'ContactName' },
        { label: 'Status', value: 'Status' },
        { label: 'Amount', value: 'Amount' },
        { label: 'Quotation Date', value: 'QuotedAt', type: 'date' },
        { label: 'Valid Till Date', value: 'DueAt', type: 'date' },
        { label: 'Lead ID', value: 'LeadId' }
    ];

    const [advancedFilters, setAdvancedFilters] = useState(null);

    const handleAdvanceFilterSubmit = (filterData) => {
        setAdvancedFilters(filterData);
    };

    const statsCounts = useMemo(() => {
        const counts = { open: 0, converted: 0, pending: 0, expired: 0 };
        allQuotations.forEach(q => {
            const status = getStandardStatus(q.Status);
            if (status === 'Approved') counts.converted++;
            else if (status === 'Draft') counts.open++;
            else if (status === 'Sent For Approval') counts.pending++;
            else if (status === 'Cancelled') counts.expired++;
        });
        return counts;
    }, [allQuotations]);

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterCriteria, setFilterCriteria] = useState({ logic: 'AND', rows: [] });

    // Detail view state
    const [detailView, setDetailView] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [showFooter, setShowFooter] = useState(true);
    const [showHistory, setShowHistory] = useState(true);

    // Edit view state (shared with QuotationActionFilteration)
    const [editView, setEditView] = useState(null);
    const [editForm, setEditForm] = useState({});

    const [currencySymbol, setCurrencySymbol] = useState('₹');
    const [viewingLeadId, setViewingLeadId] = useState(null);

    // Ref to call action methods from the action component
    const actionRef = useRef(null);
    const dropdownRef = useRef(null);

    const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
    const GET_QUOTATION_LIST = `${NEWV3_BASE_URL}/${API_ENDPOINTS.Quotation.GET_QUOTATION_LIST_BYID}`.trim();
    const API_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || 'https://wcfkit19.azurewebsites.net').replace(/\/$/, '');
    const SAVE_APPROVAL_URL = `${NEWV3_BASE_URL}${API_ENDPOINTS.INVOICE.SAVE_APPROVAL_SETTING}`.trim();

    const TOKEN = API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN;

    const getToken = () => {
        try {
            return API_ENDPOINTS.TOKEN?.UNIVERSAL_TOKEN || API_ENDPOINTS.TOKE?.UNIVERSAL_TOKEN || localStorage.getItem('Token') || '';
        } catch (e) {
            return localStorage.getItem('Token') || '';
        }
    };

    const getInitials = (name) => {
        if (!name || name === 'N/A' || name === 'null') return '?';
        const trimmed = name.trim();

        // Handle phone numbers starting with + (e.g. +98451 → "98")
        if (trimmed.startsWith('+')) {
            return trimmed.substring(1).replace(/\D/g, '').substring(0, 2);
        }

        // Handle pure phone numbers (digits only, e.g. 9032378 → "90")
        const digitsOnly = trimmed.replace(/\D/g, '');
        if (digitsOnly.length >= 4 && /^\+?\d[\d\s\-()]+$/.test(trimmed)) {
            return digitsOnly.substring(0, 2);
        }

        const parts = trimmed.split(/\s+/);
        // Single word: only the first letter (e.g. "Shubham" → "S")
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        // Multi-word: first letter of first word + first letter of last word (e.g. "Shubham Mane" → "SM")
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    };

    const getAvatarColor = (initials) => {
        // Refined vibrant colors that work well as text on light backgrounds
        const colors = [
            '#4f46e5', // Indigo
            '#059669', // Emerald
            '#d97706', // Amber
            '#dc2626', // Red
            '#7c3aed', // Violet
            '#0891b2', // Cyan
            '#db2777'  // Pink
        ];
        if (!initials || initials === '?') return '#64748b';
        const charCodeSum = initials.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
        return colors[charCodeSum % colors.length];
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setActiveDropdownId(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Handle incoming actions from other modules (e.g., EntityEdit)
    useEffect(() => {
        const action = searchParams.get('action');
        if (action === 'add' && actionRef.current) {
            const leadId = searchParams.get('leadId');
            const timer = setTimeout(() => {
                actionRef.current.handleAddQuotation(leadId);
                // Clear search params to avoid re-triggering on manual refresh
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('action');
                newParams.delete('leadId');
                setSearchParams(newParams, { replace: true });
            }, 600);
            return () => clearTimeout(timer);
        }
    }, [searchParams, setSearchParams]);

    // Search Debouncing
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchQuotations = async () => {
        setLoading(true);
        setHasFetched(false);
        try {
            const userId = localStorage.getItem('userId') || '34594';
            const payload = {
                Token: "-2295521862261168",
                Details: {
                    UserId: parseInt(userId, 10),
                    FullText: "",
                    FilterText: "",
                    OperationMode: "",
                    Status: "",
                    OffsetValue: 0,
                    PagingSize: 5000
                }
            };

            const response = await fetch(GET_QUOTATION_LIST, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const parsed = await response.json();
                const data = parsed?.Details || parsed?.d || parsed?.Data || parsed?.data || parsed;
                const list = Array.isArray(data) ? data : [];
                setAllQuotations(list);

                // Capture dynamic currency symbol
                if (list.length > 0 && list[0].CurrencySymbol) {
                    setCurrencySymbol(list[0].CurrencySymbol);
                } else if (parsed.CurrencySymbol) {
                    setCurrencySymbol(parsed.CurrencySymbol);
                }
            }
        } catch (error) {
            console.error('Error fetching quotations:', error);
        } finally {
            setLoading(false);
            setHasFetched(true);
            setIsInitialLoad(false);
            if (statusFilters.length === 0) {
                // No longer need to fetch summary stats separately
            }
        }
    };


    useEffect(() => {
        setLoading(true);
        fetchQuotations();
    }, []); // Run only once on mount


    // --- Logic for Frontend Filtering & Stats ---
    const filteredQuotations = useMemo(() => {
        let result = allQuotations;

        // 1. Status Filters
        if (statusFilters.length > 0) {
            result = result.filter(q => statusFilters.includes(getStandardStatus(q.Status)));
        }

        // 2. Search Filter
        if (debouncedSearchTerm) {
            const search = debouncedSearchTerm.toLowerCase();
            result = result.filter(q =>
                String(q.QuoteNumber || q.Number || '').toLowerCase().includes(search) ||
                String(q.ContactName || q.CustomerName || '').toLowerCase().includes(search) ||
                String(q.MobileNo || q.ContactPhone || '').toLowerCase().includes(search) ||
                String(q.LeadNo || '').toLowerCase().includes(search)
            );
        }

        // 3. Advanced Filter from SubHeader
        if (advancedFilters && advancedFilters.rows && advancedFilters.rows.length > 0) {
            result = result.filter(q => {
                const results = advancedFilters.rows.map(row => {
                    const fieldVal = q[row.field];
                    const val = String(fieldVal || '').toLowerCase();
                    const target = String(row.value || '').toLowerCase();

                    switch (row.operator) {
                        case 'equals': return val === target;
                        case 'not_equal': return val !== target;
                        case 'contains': return val.includes(target);
                        case 'not_contains': return !val.includes(target);
                        case 'starts_with': return val.startsWith(target);
                        case 'ends_with': return val.endsWith(target);
                        default: return true;
                    }
                });

                return advancedFilters.logic === 'ALL'
                    ? results.every(r => r)
                    : results.some(r => r);
            });
        }

        return result;
    }, [allQuotations, statusFilters, debouncedSearchTerm, advancedFilters]);

    const totalRecords = filteredQuotations.length;

    const allStatusStats = useMemo(() => {
        // Compute counts based on filtered results (except for the current filter itself if desired, 
        // but typically dashboard cards should reflect the current filtered set or the global set)
        // User's previous feedback was to show filtered counts.
        const currentStats = { total: filteredQuotations.length, open: 0, converted: 0, pending: 0, expired: 0 };
        filteredQuotations.forEach(q => {
            const status = getStandardStatus(q.Status);
            if (status === 'Approved') currentStats.converted++;
            else if (status === 'Draft') currentStats.open++;
            else if (status === 'Sent For Approval') currentStats.pending++;
            else if (status === 'Cancelled') currentStats.expired++;
        });

        return {
            total: currentStats.total,
            draft: currentStats.open,
            approved: currentStats.converted,
            pending: currentStats.pending,
            cancelled: currentStats.expired
        };
    }, [filteredQuotations]);
    const paginatedQuotations = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredQuotations.slice(start, start + itemsPerPage);
    }, [filteredQuotations, currentPage, itemsPerPage]);


    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm, advancedFilters, statusFilters]);

    const handleStatusFilter = (status) => {
        if (status === 'ResetAll') {
            setStatusFilters([]);
            setSearchTerm('');
            setAdvancedFilters(null);
            setFilterCriteria({ logic: 'AND', rows: [] });
            setCurrentPage(1);
            return;
        }

        if (status === 'All') {
            setStatusFilters([]);
        } else {
            setStatusFilters(prev => {
                if (prev.includes(status)) {
                    return prev.filter(s => s !== status);
                } else {
                    return [...prev, status];
                }
            });
        }
    };

    const calendarEvents = useMemo(() => {
        return filteredQuotations.map(q => {
            const eventDate = q.QuotedAt || q.QuotationDate || q.Date || q.InvoiceDate;
            const parsed = parseCustomDate(eventDate);
            const rawStatus = String(q.Status || '').toLowerCase();

            // Map to standard keys for DynamicCalendar visibility
            let computedStatus = 'draft';
            if (rawStatus.includes('approved')) computedStatus = 'approved';
            else if (rawStatus.includes('sent') || rawStatus.includes('pend')) computedStatus = 'sent-for-approval';
            else if (rawStatus.includes('canc') || rawStatus.includes('rej') || rawStatus.includes('expir')) computedStatus = 'cancelled';

            return {
                id: q.Id || q.QuotationId || q.ID,
                title: `${q.QuoteNumber || q.Number} - ${q.ContactName || q.CustomerName || '-'}`,
                date: parsed || new Date(),
                status: computedStatus,
                computedStatus: computedStatus,
                amount: q.Amount || q.TotalAmount,
                ...q
            };
        });
    }, [filteredQuotations]);

    const quotationStatusConfig = {
        'approved': '#10b981',
        'sent-for-approval': '#f59e0b',
        'sent': '#16a34a',
        'draft': '#3b82f6',
        'cancelled': '#ef4444',
        'rejected': '#ef4444',
        'deleted': '#ef4444',
        'expired': '#ef4444'
    };

    const QuotationCalendarLegend = () => (
        <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap', gap: '12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10b981' }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Approved</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Sent For Approval</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#3b82f6' }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Draft</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Cancelled/Rejected/Deleted</Typography>
            </Box>
        </Stack>
    );

    const renderQuotationCalendarEvent = (event, view) => {
        const color = quotationStatusConfig[event.computedStatus] || '#6366f1';
        const customerName = event.ContactName || event.CustomerName || 'N/A';
        const createdBy = event.AgentName || event.CreatedBy || '-';

        // Match specific pale backgrounds from the requested design
        const bgColors = {
            'approved': '#f0fdf4',
            'draft': '#eff6ff',
            'sent-for-approval': '#fffbeb',
            'pending': '#fffbeb',
            'cancelled': '#fef2f2',
            'expired': '#fef2f2',
            'sent': '#eef2ff',
            'mark-sent': '#eef2ff',
            'mark-as-sent': '#eef2ff',
            'converted': '#f0fdf4',
            'won': '#f0fdf4'
        };

        const bgColor = bgColors[event.computedStatus] || '#f8fafc';

        // Standardized Card Design for All Views (Matches Task Module)
        return (
            <Box
                onClick={(e) => {
                    // Main card click - show details if not clicking a link inside
                    e.stopPropagation();
                    if (e.target.tagName !== 'P' && e.target.tagName !== 'SPAN' && e.target.tagName !== 'IMG') {
                        handleShowQuotation(event);
                    }
                }}
                sx={{
                    bgcolor: bgColor,
                    borderLeft: `4px solid ${color}`,
                    borderRadius: '8px',
                    p: '10px 12px',
                    mb: '8px',
                    cursor: 'default',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    width: '100%',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    flexShrink: 0,
                    '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        transform: 'translateY(-1px)',
                    }
                }}
            >
                {/* Quotation Number Title -> Show Functionality */}
                <Typography
                    onClick={(e) => { e.stopPropagation(); handleShowQuotation(event); }}
                    sx={{
                        fontSize: '13px', fontWeight: 800, color: '#2563eb',
                        cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden',
                        textOverflow: 'ellipsis', width: '100%',
                        '&:hover': { textDecoration: 'underline' }
                    }}
                >
                    {event.QuoteNumber || event.Number || 'QUO-XXX'}
                </Typography>

                {/* Horizontal Info Row (Identical to reference image) */}
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', overflow: 'hidden' }}>
                    {/* Customer Info -> Lead Details Overlay */}
                    <Box
                        onClick={(e) => { e.stopPropagation(); setViewingLeadId(event.LeadId); }}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            minWidth: 0, flexShrink: 1, cursor: 'pointer',
                            '&:hover p': { textDecoration: 'underline' }
                        }}
                    >
                        <Typography sx={{
                            fontSize: '11px', fontWeight: 600, color: '#2563eb', // Customer in Blue
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            minWidth: 0
                        }}>
                            {customerName}
                        </Typography>
                    </Box>

                    {/* Creator Info (No Linking) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, flexShrink: 1 }}>
                        <Box sx={{
                            width: 24, height: 24, borderRadius: '50%', border: '1px solid #e2e8f0',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff',
                            flexShrink: 0
                        }}>
                            <Icon icon="ri:user-3-line" fontSize={14} color="#94a3b8" />
                        </Box>
                        <Typography sx={{
                            fontSize: '11px', fontWeight: 600, color: '#1e293b', // Creator in Black/Dark Slate
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            minWidth: 0
                        }}>
                            {createdBy}
                        </Typography>
                    </Box>
                </Stack>
            </Box>
        );
    };

    const handleEditQuotation = (quotation) => {
        setActiveDropdownId(null);
        if (actionRef.current) actionRef.current.handleEditQuotation(quotation);
    };

    const handleDuplicateQuotation = (quotation) => {
        setActiveDropdownId(null);
        if (actionRef.current) actionRef.current.handleDuplicateQuotation(quotation);
    };

    const handleAddQuotation = () => {
        setActiveDropdownId(null);
        if (actionRef.current) actionRef.current.handleAddQuotation();
    };

    const handleRequestApproval = (quotation) => {
        setActiveDropdownId(null);
        if (actionRef.current) actionRef.current.handleRequestApproval(quotation);
    };

    const handleShowQuotation = (quotation) => {
        setActiveDropdownId(null);
        if (actionRef.current) actionRef.current.handleShowQuotation(quotation);
    };

    const handleDeleteQuotation = (quotation) => {
        setActiveDropdownId(null);
        if (actionRef.current) actionRef.current.handleDeleteQuotation(quotation);
    };

    const handleCancelQuotation = (quotation) => {
        setActiveDropdownId(null);
        if (actionRef.current) actionRef.current.handleCancelQuotation(quotation);
    };

    const handlePrint = (quotation) => {
        setActiveDropdownId(null);
        if (actionRef.current) actionRef.current.handlePrint(quotation);
    };

    const handleMarkAsSent = (quotation) => {
        setActiveDropdownId(null);
        if (actionRef.current) actionRef.current.handleMarkAsSent(quotation);
    };

    const handleApplyFilter = (criteria) => {
        setFilterCriteria(criteria);
        setCurrentPage(1);
    };

    const handleClearFilter = () => {
        setFilterCriteria({ logic: 'AND', rows: [] });
        setCurrentPage(1);
    };

    const handleExportToExcel = () => {
        if (!filteredQuotations || filteredQuotations.length === 0) {
            Swal.fire({
                title: 'No Data',
                text: 'There are no quotations to export.',
                icon: 'info',
                confirmButtonColor: '#3b82f6'
            });
            return;
        }

        try {
            // Prepare data for Excel with correct mappings based on column definitions
            const exportData = filteredQuotations.map((q, index) => ({
                'SNo.': index + 1,
                'Quotation Number': q.QuoteNumber || q.QuotationNumber || q.Number || '-',
                'Status': q.Status || 'Draft',
                'Quotation Date': formatDate(q.QuotedAt || q.QuotationDate || q.Date),
                'Valid Till': formatDate(q.DueAt || q.ValidTillDate || q.ExpiryDate),
                'Customer Name': q.ContactName || q.CustomerName || q.PersonName || 'NA',
                'Customer Email': q.ContactEmail || q.Email || '-',
                'Customer Phone': q.ContactPhone || q.MobileNo || q.Mobile || '-',
                'Amount': q.Amount || q.TotalAmount || 0,
                'Lead No': q.LeadNo || q.LeadId || '-',
                'Created On': formatDate(q.CreatedOn || q.CreatedAt),
                'Created By': q.CreatedBy || q.UserName || '-'
            }));

            // Create worksheet
            const ws = XLSX.utils.json_to_sheet(exportData);

            // Set column widths
            const colWidths = [
                { wch: 6 },  // SNo
                { wch: 20 }, // Quotation Number
                { wch: 15 }, // Status
                { wch: 15 }, // Quotation Date
                { wch: 15 }, // Valid Till
                { wch: 25 }, // Customer Name
                { wch: 30 }, // Customer Email
                { wch: 15 }, // Customer Phone
                { wch: 12 }, // Amount
                { wch: 10 }, // Lead No
                { wch: 15 }, // Created On
                { wch: 20 }  // Created By
            ];
            ws['!cols'] = colWidths;

            // Create workbook and append sheet
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Quotations');

            // Save file
            XLSX.writeFile(wb, `Quotations_Export_${new Date().toISOString().split('T')[0]}.xlsx`);

            Swal.fire({
                title: 'Export Successful',
                text: 'Quotation list has been downloaded as Excel.',
                icon: 'success',
                confirmButtonColor: '#3b82f6',
                timer: 2000
            });
        } catch (error) {
            console.error("Export error:", error);
            Swal.fire({
                title: 'Export Failed',
                text: 'An error occurred while generating the Excel file.',
                icon: 'error',
                confirmButtonColor: '#3b82f6'
            });
        }
    };

    // Pagination Helper
    const totalPages = Math.ceil(totalRecords / itemsPerPage);

    const getPaginationRange = (current, total) => {
        if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
        if (current <= 4) return [1, 2, 3, 4, 5, '...', total];
        if (current >= total - 3) return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
        return [1, '...', current - 1, current, current + 1, '...', total];
    };

    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const formatCurrency = (amount) => {
        const val = parseFloat(amount) || 0;
        const formatted = new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(val);
        return `${currencySymbol} ${formatted}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return dateStr;

            const day = String(date.getDate()).padStart(2, '0');
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = monthNames[date.getMonth()];
            const year = date.getFullYear();

            return `${day}-${month}-${year}`;
        } catch { return dateStr; }
    };

    const quotationCustomCards = [
        {
            title: (statusFilters.length === 0) ? (allStatusStats.total ?? '-') : 0,
            subtitle: 'Total Quotations', icon: 'ri:file-list-3-line', color: 'primary', key: 'All'
        },
        {
            title: (statusFilters.length === 0 || statusFilters.includes('Approved')) ? (allStatusStats.approved ?? '-') : 0,
            subtitle: 'Approved', icon: 'ri:checkbox-circle-line', color: 'success', key: 'Approved'
        },
        {
            title: (statusFilters.length === 0 || statusFilters.includes('Draft')) ? (allStatusStats.draft ?? '-') : 0,
            subtitle: 'Draft', icon: 'ri:edit-line', color: 'info', key: 'Draft'
        },
        {
            title: (statusFilters.length === 0 || statusFilters.includes('Sent For Approval')) ? (allStatusStats.pending ?? '-') : 0,
            subtitle: 'Sent for Approval', icon: 'ri:time-line', color: 'warning', key: 'Sent For Approval'
        },
        {
            title: (statusFilters.length === 0 || statusFilters.includes('Cancelled')) ? (allStatusStats.cancelled ?? '-') : 0,
            subtitle: 'Expired/Cancelled', icon: 'ri:close-circle-line', color: 'error', key: 'Cancelled'
        }
    ];

    // --- Premium Column Definitions ---
    const columns = useMemo(() => [
        {
            id: 'Number',
            header: 'Quotation Number',
            accessorKey: 'QuoteNumber',
            cell: info => {
                const q = info.row.original;
                return (
                    <div className="quotation-number-cell"
                        onClick={() => handleShowQuotation(q)}
                        style={{ cursor: 'pointer' }}>
                        <div className="quotation-icon-circle">
                            <FileText size={14} />
                        </div>
                        <span className="customer-name-link-premium" style={{ fontSize: '13px' }}>
                            {q.QuoteNumber || q.QuotationNumber || q.Number}
                        </span>
                    </div>
                )
            }
        },
        {
            id: 'Customer',
            header: 'Customer',
            accessorKey: 'ContactName',
            cell: info => {
                const q = info.row.original;
                const name = q.ContactName || q.CustomerName || q.PersonName || 'NA';
                const initials = getInitials(name);
                const avatarColor = getAvatarColor(initials);
                const mobile = q.ContactPhone || q.MobileNo || q.Mobile || '-';

                return (
                    <div className="customer-col-content-premium" style={{ gap: '12px' }}>
                        <ScoreCard
                            leadId={q.LeadId || q.LeadNo}
                            mobileNo={mobile}
                            personName={name}
                            initialScore={q.CurrentScore}
                        >

                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: `${avatarColor}15`,
                                        color: avatarColor,
                                        fontSize: '12px',
                                        fontWeight: 800,
                                        cursor: 'pointer',
                                        border: `1px solid ${avatarColor}20`,
                                        textShadow: 'none',
                                        position: 'relative'
                                    }}
                                >
                                    {initials}
                                </Avatar>

                        </ScoreCard>

                        <div className="customer-details-meta-premium" style={{ marginLeft: 0 }}>
                            <div className="customer-name-group-modern">
                                <span className="customer-name-link-premium"
                                    style={{ fontSize: '14px' }}
                                    onClick={(e) => { e.stopPropagation(); setViewingLeadId(q.LeadId || q.LeadNo); }}>
                                    {name}
                                </span>
                            </div>
                            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
                                {(q.LeadNo || q.LeadId) && (
                                    <>
                                        Lead No. {q.LeadNo || q.LeadId}
                                    </>
                                )}
                            </Typography>
                        </div>
                    </div>
                )
            }
        },
        {
            id: 'Mobile',
            header: 'Mobile Number',
            accessorKey: 'ContactPhone',
            cell: info => {
                const q = info.row.original;
                return <span style={{ color: '#475569', fontWeight: 500 }}>{q.ContactPhone || q.MobileNo || q.Mobile || '-'}</span>
            }
        },
        {
            id: 'Amount',
            header: 'Amount',
            accessorKey: 'Amount',
            cell: info => <span style={{ fontWeight: 700, color: '#1e293b' }}>{formatCurrency(info.row.original.Amount || info.row.original.TotalAmount)}</span>
        },
        {
            id: 'QuotationDate',
            header: 'Quotation Date',
            accessorKey: 'QuotationDate',
            cell: info => formatDate(info.row.original.QuotedAt || info.row.original.QuotationDate || info.row.original.Date)
        },
        {
            id: 'ValidTillDate',
            header: 'Valid Till Date',
            accessorKey: 'DueAt',
            cell: info => formatDate(info.row.original.DueAt || info.row.original.ValidTillDate || info.row.original.ExpiryDate)
        },
        {
            id: 'Status',
            header: 'Status',
            accessorKey: 'Status',
            cell: info => {
                const status = String(info.getValue() || 'Draft').toLowerCase().trim();
                const statusClass = status.replace(/\s+/g, '-');
                return (
                    <span className={`status-pill-luxury ${statusClass}`}>
                        {info.getValue() || 'Draft'}
                    </span>
                )
            }
        },
        {
            id: 'action',
            header: 'Action',
            cell: info => (
                <div className="action-icons-group" style={{ justifyContent: 'center' }}>
                    <QuotationRowActions
                        quotation={info.row.original}
                        crmOperationPerms={crmOperationPerms}
                        onEdit={handleEditQuotation}
                        onDuplicate={handleDuplicateQuotation}
                        onRequestApproval={handleRequestApproval}
                        onShow={handleShowQuotation}
                        onDelete={handleDeleteQuotation}
                        onCancel={handleCancelQuotation}
                        onPrint={handlePrint}
                        onMarkAsSent={handleMarkAsSent}
                    />
                </div>
            )
        }
    ], [filteredQuotations]);

    return (
        <div className="quotation-module-container">

            <SubHeader
                pageName="Quotations"
                pageCode="PAG10222"
                title="Quotation List"
                customPadding="0 20px"
                totalCount={filteredQuotations.length}
                searchQuery={searchTerm}
                setSearchQuery={setSearchTerm}
                searchPlaceholder="Search quotations..."
                viewMode={viewMode}
                setViewMode={setViewMode}
                onAddClick={crmOperationPerms?.Add !== false ? handleAddQuotation : null}
                showSearch={!!crmOperationPerms?.Search}
                showExport={false}
                showViewSwitcher={true}
                showGrid={false}
                showTable={true}
                showCalendar={true}
                showSettings={true}
                advanceFilterFields={advanceFilterFields}
                advancedFilters={advancedFilters}
                onApplyFilter={handleApplyFilter}
                onClearFilter={handleClearFilter}
                customMenuItems={
                    <>
                        {crmOperationPerms?.Export !== false && (
                            <button
                                className="menu-action-btn"
                                onClick={handleExportToExcel}
                                title="Export"
                            >
                                <PiExport size={20} />
                            </button>
                        )}
                        <button
                            className="menu-action-btn"
                            onClick={() => setIsFilterOpen(true)}
                            title="Advanced Filter"
                        >
                            <Filter size={20} />
                        </button>
                    </>
                }
            />

            {viewMode !== 'calendar' && (
                <div style={{ position: 'relative', zIndex: 1100, overflow: 'visible' }}>
                    <PremiumModuleCards
                        stats={allStatusStats}
                        onStatusClick={handleStatusFilter}
                        moduleName="Quotation"
                        customCards={quotationCustomCards}
                        activeFilters={statusFilters}
                    />
                </div>
            )}

            {viewMode === 'table' || viewMode === 'grid' ? (
                <div className="quotation-table-container" style={{ position: 'relative' }}>
                    {loading && (
                        <div style={{
                            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: '#ffffff',
                            zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <Loader message="Loading quotations..." />
                        </div>
                    )}
                    <PremiumModuleTable
                        data={paginatedQuotations}
                        columns={columns}
                        totalRecords={totalRecords}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                        loading={loading || isInitialLoad}
                        hasFetched={hasFetched && !isInitialLoad}
                        globalFilter={searchTerm}
                    />
                </div>
            ) : (
                <div className="quotation-calendar-view" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <DynamicCalendar
                        events={calendarEvents}
                        onEventClick={handleShowQuotation}
                        isLoading={loading}
                        statusConfig={quotationStatusConfig}
                        entityLabel="quotation"
                        headerLegend={<QuotationCalendarLegend />}
                        eventRenderer={renderQuotationCalendarEvent}
                    />
                </div>
            )}

            {/* Centralized Lead Details Overlay */}
            {viewingLeadId && (
                <div className="lead-details-overlay-premium" style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    zIndex: 10000, background: '#fff'
                }}>
                    <CommonLeadDetails
                        leadId={viewingLeadId}
                        onBack={() => setViewingLeadId(null)}
                        module="quotation"
                    />
                </div>
            )}
            {/* Overlays and Modals - Rendered at the bottom for correct stacking order */}
            <QuotationActionFilteration
                ref={actionRef}
                fetchQuotations={fetchQuotations}
                editView={editView}
                setEditView={setEditView}
                editForm={editForm}
                setEditForm={setEditForm}
                getToken={getToken}
                crmOperationPerms={crmOperationPerms}
            />

            <QuotationAdvancedFilter
                isOpen={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}
                onApply={handleApplyFilter}
                onClear={handleClearFilter}
            />
        </div>
    );
};

// --- Premium Row Actions Component with MUI Menu ---
const QuotationRowActions = ({
    quotation, crmOperationPerms, onEdit, onDuplicate, onRequestApproval, onShow, onDelete, onCancel, onPrint, onMarkAsSent
}) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (e) => {
        if (e) e.stopPropagation();
        setAnchorEl(null);
    };

    const status = String(quotation.Status || '').toLowerCase();
    const isApproved = status === 'approved';
    const isDeleted = status === 'deleted' || status === 'delete';
    const isLocked = ['sent-for-approval', 'sent for approval', 'approved', 'cancelled', 'sent', 'rejected', 'deleted'].includes(status);
    const canModify = !isLocked;

    if (crmOperationPerms && !crmOperationPerms.Edit && !crmOperationPerms.Delete && !crmOperationPerms.View) {
        return null;
    }

    return (
        <div onClick={(e) => e.stopPropagation()}>
            <IconButton
                aria-label="more"
                id="long-button"
                aria-controls={open ? 'long-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleClick}
                size="small"
                sx={{
                    color: '#94a3b8',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: '#f1f5f9', color: '#6366f1' }
                }}
            >
                <MoreVertical size={18} />
            </IconButton>
            <Menu
                id="long-menu"
                MenuListProps={{ 'aria-labelledby': 'long-button' }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 8px 24px rgba(148, 163, 184, 0.15))',
                        mt: 1,
                        borderRadius: '12px',
                        minWidth: '200px',
                        border: '1px solid #f1f5f9',
                        '& .MuiMenuItem-root': {
                            fontSize: '13px',
                            fontWeight: 500,
                            padding: '10px 16px',
                            gap: '4px',
                            color: '#475569',
                            transition: 'all 0.2s',
                            '&:hover': {
                                bgcolor: '#f8fafc',
                                color: '#1e293b'
                            }
                        },
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                            borderLeft: '1px solid #f1f5f9',
                            borderTop: '1px solid #f1f5f9',
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {/* Show — always visible */}
                <MenuItem onClick={() => { handleClose(); onShow(quotation); }}>
                    <ListItemIcon><Eye size={16} color="#06b6d4" /></ListItemIcon>
                    <ListItemText>Show</ListItemText>
                </MenuItem>

                {/* Duplicate & other actions hidden if deleted */}
                {!isDeleted && (
                    <>
                        {crmOperationPerms?.Add !== false && (
                            <MenuItem onClick={() => { handleClose(); onDuplicate(quotation); }}>
                                <ListItemIcon><Copy size={16} color="#8b5cf6" /></ListItemIcon>
                                <ListItemText>Duplicate</ListItemText>
                            </MenuItem>
                        )}

                        {crmOperationPerms?.Edit !== false && !isApproved && canModify && status !== 'rejected' && status !== 'reject' && (
                            <MenuItem onClick={() => { handleClose(); onRequestApproval(quotation); }}>
                                <ListItemIcon><FileText size={16} color="#3b82f6" /></ListItemIcon>
                                <ListItemText>Request Approval</ListItemText>
                            </MenuItem>
                        )}

                        {crmOperationPerms?.Edit !== false && !isApproved && canModify && (
                            <MenuItem onClick={() => { handleClose(); onEdit(quotation); }}>
                                <ListItemIcon><Edit3 size={16} color="#f59e0b" /></ListItemIcon>
                                <ListItemText>Edit</ListItemText>
                            </MenuItem>
                        )}

                        {crmOperationPerms?.Edit !== false && !isApproved && status !== 'sent' && (
                            <MenuItem onClick={() => { handleClose(); onMarkAsSent(quotation); }}>
                                <ListItemIcon><Send size={16} color="#10b981" /></ListItemIcon>
                                <ListItemText>Mark as Sent</ListItemText>
                            </MenuItem>
                        )}

                        {!isApproved && canModify && (
                            <>
                                <Divider sx={{ my: '4px !important', opacity: 0.6 }} />
                                {crmOperationPerms?.Edit !== false && (
                                    <MenuItem onClick={() => { handleClose(); onCancel(quotation); }} sx={{ color: '#ef4444 !important' }}>
                                        <ListItemIcon><X size={16} color="#ef4444" /></ListItemIcon>
                                        <ListItemText>Cancel</ListItemText>
                                    </MenuItem>
                                )}
                                {crmOperationPerms?.Delete !== false && (
                                    <MenuItem onClick={() => { handleClose(); onDelete(quotation); }} sx={{ color: '#ef4444 !important' }}>
                                        <ListItemIcon><Trash2 size={16} color="#ef4444" /></ListItemIcon>
                                        <ListItemText>Delete</ListItemText>
                                    </MenuItem>
                                )}
                            </>
                        )}
                    </>
                )}
            </Menu>
        </div>
    );
};

export default QuotationsModule;
